import { Column } from "@components/table/table";
import { useNfe } from "@contexts/nfe";
import { Tooltip } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomTNFeInfNFeIDENFref, defaultCustomNFref, defColumns, defineColValue, NfeTabProps, StyledNfRefTable, TableAction } from "./helper/nfeRefTabHelper";
import { NfRefModal } from "../modal/NfRefModal";
import Modal from "@components/modal/modal";


export function NfeRefTab({ activeNFe, editMode }: NfeTabProps) {

    const { editedNfe, updateEditedNfe } = useNfe();

    const defaultRefs = editMode ?
        editedNfe?.nfref :
        activeNFe.infNfe?.ide.nfref?.map(item => ({ ...item, id: crypto.randomUUID() }));

    const [data, setData] = useState<CustomTNFeInfNFeIDENFref[]>(defaultRefs || []);
    const [columns, setColumns] = useState<Column[]>(defColumns);
    const [nFref, setnNFref] = useState<CustomTNFeInfNFeIDENFref>();
    const [removeItem, setRemoveItem] = useState<CustomTNFeInfNFeIDENFref>();

    const actions = useMemo<TableAction[]>(() => [
        {
            html: <i className="bi bi-pencil table-action action-positive"></i>,
            callback: (nfRef: CustomTNFeInfNFeIDENFref) => setnNFref(nfRef),
            tooltipText: 'Editar'
        },
        {
            html: <i className="bi bi-trash table-action action-positive" />,
            callback: (nfRef: CustomTNFeInfNFeIDENFref) => setRemoveItem(nfRef),
            tooltipText: 'Remover'
        }
    ], [data, editedNfe]);

    const updateNFRefs = useCallback((ref: CustomTNFeInfNFeIDENFref): void => {
        delete ref.nfType;
        if (!ref.id) {
            ref.id = crypto.randomUUID();
        }

        const refs = structuredClone(data || []);
        const index = refs.findIndex(r => r.id === ref.id);
        if (index !== -1) {
            refs[index] = ref;
        } else {
            refs.push(ref);
        }

        setData(refs);
        updateEditedNfe({ ...editedNfe, nfref: refs });
    }, [data, setData, editedNfe, updateEditedNfe]);

    useEffect(() => {
        if (!editMode) {
            setData(defaultRefs || []);
        }
    }, [editMode]);

    return <>
        <aside className={['bottom-frame', 'edit-border', 'visible'].join(' ')}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="header">Grupo de informações da NF referenciada</span>
                {editMode && <button type="button" className={`icon-button`} style={{ alignItems: 'center' }} onClick={() => setnNFref(defaultCustomNFref)}>Adicionar</button>}
            </div>
            <div className="content">
                <StyledNfRefTable listLength={data.length}>
                    {/* HEADER */}
                    <section className="header-frame" >
                        <div className="header-item-frame" style={{ width: '100%', maxWidth: '165px' }}>
                            <span>Tipo de Referência</span>
                        </div>
                        <div className="header-item-frame" style={{ marginLeft: 10, minWidth: "fit-content" }}>
                            <span>Descrição</span>
                        </div>
                        {editMode &&
                            <div className="header-item-frame" style={{ minWidth: 165, width: 165, justifyContent: "end", flex: 1 }}>
                                <span>Ações</span>
                            </div>
                        }
                    </section>

                    {data.length ? data.map((row, index) => {
                        return (
                            <section className="list-frame" style={{ display: 'flex', justifyContent: "space-between", flexDirection: 'row' }} key={'list-frame-' + index}>
                                <div key={'row-frame-' + index} className="row-frame" style={{ display: 'flex', backgroundColor: 'white' }}>
                                    {columns.map(col => {
                                        if (col.isActive) {
                                            if (col.customView) {
                                                if (typeof col.customView === 'function') {
                                                    return <div key={col.accessor} className="row-item" style={{ minWidth: col.minWidth, width: '100%', maxWidth: '165px' }}>{(col.customView && col.customView(row)) || (row[col.accessor] || "N/A")}</div>;
                                                }
                                                return col.customView;
                                            } else {
                                                return (
                                                    <div key={col.accessor} className="row-item" style={{ minWidth: col.minWidth, width: '100%' }}>
                                                        {defineColValue(row)}
                                                    </div>
                                                );
                                            }
                                        }
                                        return null;
                                    })}
                                </div>
                                {editMode &&
                                    <div key={'row-frame-actions-' + index} className="row-frame" style={{ display: 'flex', padding: 0, margin: 0, backgroundColor: 'white' }}>
                                        <div className="actions-frame" style={{ minWidth: "fit-content" }}>
                                            {actions.map((action, index) => {
                                                return <Tooltip title={action.tooltipText} placement="top" key={index}>
                                                    <button className={`action-frame`} key={index} onClick={() => action.callback(row)}>
                                                        {action.html}
                                                    </button>
                                                </Tooltip>;
                                            })}
                                        </div>
                                    </div>}
                            </section>
                        )
                    }) :
                        <section className="list-frame" style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="loading">Nenhum resultado encontrado</div>
                        </section>
                    }
                </StyledNfRefTable>
            </div>
        </aside>

        {nFref && <NfRefModal nFref={nFref} handleClose={() => setnNFref(undefined)} onSave={updateNFRefs} />}

        {removeItem &&
            <Modal
                style={{ top: '6px', left: '-14px', width: '100%', position: 'absolute', zIndex: '10000' }}
                headerTitle="Confirmar remoção"
                closeOnClickAway={false}
                handleClose={() => setRemoveItem(undefined)}
                loading={false}
                actionButtons={[{
                    text: 'Cancelar',
                    actionCallback: () => setRemoveItem(undefined),
                    positiveAction: false
                },
                {
                    text: 'Confirmar',
                    actionCallback: () => {
                        const refs = data.filter(ref => ref.id !== removeItem?.id);
                        setData(refs);
                        updateEditedNfe({ ...editedNfe, nfref: refs });
                        setRemoveItem(undefined)
                    },
                    positiveAction: true
                }]}>
                <div style={{ display: "flex", flexDirection: "column", textAlign: "center" }}>
                    <span>Deseja realmente remover a referência?</span>
                    <span>Lembrando que os dados alterados para esse registro serão perdidos.</span>
                </div>
            </Modal>
        }
    </>;
}